import { Stack } from "@mui/material";
import { useTheme } from "design-system/themeContext";
import { useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useQueryClient } from "react-query";
import { useParams, useSearchParams } from "react-router-dom";

import { useGetTechData } from "../api/hooks/allHooks";
import { NavMenu } from "../components/TechnicalData/NavMenu";
import { TechTable } from "../components/TechnicalData/TechTable";
import { techDataLabelsOrder, techDataSections } from "../configs/TechnicalDataEnums";
import { ScenarioSelector } from "../features/technical-data/scenario/components/ScenarioSelector";
import { TechnicalDataScenarioProvider } from "../features/technical-data/scenario/context/TechnicalDataScenarioProvider";
import {
  getCustomTechDataTabNames,
  getLegacyTechDataTabNames,
  isCustomTechDataTab,
} from "../features/technical-data/technicalDataTabRegistry";
import { eventNames, trackGAEvent, withGAEventTracking } from "../functions/googleAnalytics";
import { extractTabData, getDefaultActiveTab, getOrderedEnabledTabs } from "../functions/TechnicalData";
import useQueryParamsState from "../hooks/states";

export const TechnicalData = () => {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const paramKey = "tab";
  const paramValue = searchParams.get(paramKey);
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useQueryParamsState(paramKey, "windows");
  const theme = useTheme();
  const { data, isError, isLoading } = useGetTechData({ projectId, tabNames: getLegacyTechDataTabNames() });
  const orderedEnabledTabs = useMemo(() => appendCustomTabs(getOrderedEnabledTabs(data)), [data]);
  const tabData = extractTabData({ activeTab, data });

  useEffect(() => {
    if (!isError) setActiveTab(paramValue || getDefaultActiveTab(orderedEnabledTabs));
  }, [isLoading, isError]);

  useEffect(() => {
    if (isCustomTechDataTab(activeTab)) return;
    queryClient.invalidateQueries({
      queryKey: ["techDataAllParents", activeTab, projectId],
    });
  }, [activeTab, projectId, queryClient]);

  useEffect(() => {
    trackGAEvent(eventNames.TD_VIEW);
  }, []);

  return (
    <>
      <Helmet>
        <title>Technical data | {theme.clientDisplayName}</title>
      </Helmet>
      <TechnicalDataScenarioProvider projectId={projectId}>
        <Stack className="flex-col items-stretch ">
          <ScenarioSelector />
          <Stack className="items-start " spacing={4}>
            <NavMenu
              activeTab={activeTab}
              className="w-60 p-0 pt-2"
              enabledTabs={orderedEnabledTabs}
              setActiveTab={(tabName) =>
                withGAEventTracking(eventNames.TD_TAB_VIEW, { tabName }, () => setActiveTab(tabName))
              }
            />
            <TechTable
              activeTab={activeTab || techDataSections.WINDOWS}
              data={tabData}
              enabledTabs={orderedEnabledTabs}
              parentsAreLoading={isLoading}
              projectId={projectId}
            />
          </Stack>
        </Stack>
      </TechnicalDataScenarioProvider>
    </>
  );
};

const appendCustomTabs = (enabledTabs) => {
  const tabs = [...(enabledTabs ?? [])];

  getCustomTechDataTabNames().forEach((tabName) => {
    if (!tabs.includes(tabName)) tabs.push(tabName);
  });

  return tabs.sort((a, b) => techDataLabelsOrder.indexOf(a) - techDataLabelsOrder.indexOf(b));
};
